import { useQuery } from '@tanstack/react-query';

import { useRequest, type QueryFunctionOptions } from '@frontend/api';

import type { DxfDrawing, DxfDrawingParams, DxfSceneMapping } from './types';

export type GetDxfSceneMappingQueryKeyType = readonly [
  'dxfSceneMapping',
  string,
];

export const getDxfSceneMappingQueryKey = (
  partFileUuid: string,
): GetDxfSceneMappingQueryKeyType => ['dxfSceneMapping', partFileUuid] as const;

/**
 * Mapping overlay for a part file. `data` is `null` until a mapping has been
 * created.
 */
export const useGetDxfSceneMapping = (
  params: DxfDrawingParams,
  {
    requestOptions,
    queryOptions,
  }: QueryFunctionOptions<
    DxfSceneMapping | null,
    GetDxfSceneMappingQueryKeyType
  > = {},
) => {
  const request = useRequest(requestOptions);
  const partFileUuid = params.partFileUuid ?? '';

  return useQuery({
    ...queryOptions,
    queryKey: getDxfSceneMappingQueryKey(partFileUuid),
    enabled: Boolean(params.partFileUuid) && (queryOptions?.enabled ?? true),
    queryFn: async ({ signal }) => {
      const drawing = await request<DxfDrawing>(`/dxf/${partFileUuid}/scene`, {
        init: {
          signal,
        },
      });
      return drawing.mapping;
    },
  });
};
